import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Dispatch } from "react";
import { Microservices } from "../../app/AllowedMicroservices";
import { RootState } from "../../app/store";
import apiActionCreator, { HttpMethods } from "../../services/apiActionCreator";
import { FetchStatus } from "../common/enums";

// Mirrors the response of GET /progress-audit/users/:username/courses
// (service-learning, SuperAdmin only).
export interface IProgressAuditCourseSummary {
  progressId: string;
  courseId: string;
  hasAudit: boolean;
  lessonsCount: number;
  lessonsFinishedCount: number;
  topicsCount: number;
  topicsFinishedCount: number;
  courseFinished: boolean;
  evaluationsPassedCount: number;
}

interface ProgressAuditCoursesState {
  list: IProgressAuditCourseSummary[];
  status: FetchStatus;
}

const initialState: ProgressAuditCoursesState = {
  list: [],
  status: FetchStatus.Idle,
};

export const progressAuditCoursesSlice = createSlice({
  name: "progressAuditCourses",
  initialState,
  reducers: {
    fetching: (state) => {
      state.status = FetchStatus.Fetching;
    },
    fetched: (state, action: PayloadAction<IProgressAuditCourseSummary[]>) => {
      state.list = action.payload || [];
      state.status = FetchStatus.Fetched;
    },
    error: (state) => {
      state.list = [];
      state.status = FetchStatus.Error;
    },
    clear: (state) => {
      state.list = [];
      state.status = FetchStatus.Idle;
    },
  },
});

export const {
  fetching,
  fetched,
  error,
  clear: clearProgressAuditCourses,
} = progressAuditCoursesSlice.actions;

export const fetchProgressAuditCoursesByUsername = (username: string) => (dispatch: Dispatch<object>) => {
  return dispatch(
    apiActionCreator({
      endpoint: `/progress-audit/users/${username}/courses`,
      types: {
        requestType: fetching,
        successTypes: [
          {
            actionOrCreator: fetched,
          },
        ],
        errorTypes: [
          {
            actionOrCreator: error,
          },
        ],
      },
      method: HttpMethods.GET,
      microservice: Microservices.LEARNING_SERVICE,
      authenticated: true,
    })
  );
};

export const selectProgressAuditCourses = (state: RootState) => state.progressAuditCourses.list || [];
export const selectIsFetchingProgressAuditCourses = (state: RootState) =>
  state.progressAuditCourses.status === FetchStatus.Fetching;
export const selectHasProgressAuditCoursesError = (state: RootState) =>
  state.progressAuditCourses.status === FetchStatus.Error;

export default progressAuditCoursesSlice.reducer;
